import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import SaveKinContactBlock from "./SaveKinContactBlock";
import { openExternal } from "@/lib/openExternal";
import {
  EXTERNAL_SUPABASE_URL,
  EXTERNAL_SUPABASE_PUBLISHABLE_KEY,
} from "@/integrations/external-supabase/client";

const SEND_SMS_URL = `${EXTERNAL_SUPABASE_URL}/functions/v1/send-sms`;

interface SmsSetupSheetProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called once check_verify returns verified: true, with the E.164 number. */
  onVerified: (phoneNumber: string) => void;
}

type Step = "phone" | "code" | "success";

/**
 * Standalone phone entry -> OTP -> verified sheet for Kin Texts You (Issue
 * #150 send-sms wiring). Reused wherever SMS opt-in is triggered (Settings
 * card here, the Morning Notes footer chip elsewhere) — this component only
 * handles the verify handshake; the caller decides what to do with the
 * verified number (e.g. flipping its own toggle prefs).
 *
 * Copy for both states is verbatim from the Family Memory Pro setup flow
 * spec so the voice matches across every place this flow appears.
 */
const SmsSetupSheet = ({ isOpen, onClose, onVerified }: SmsSetupSheetProps) => {
  const { session } = useAuth();
  const [mounted, setMounted] = useState(false);
  const [shown, setShown] = useState(false);
  const [step, setStep] = useState<Step>("phone");
  const [phoneDigits, setPhoneDigits] = useState(""); // raw 10-digit US input
  const [code, setCode] = useState("");
  // A2P 10DLC (error 30923, Forced Consent Violation): consent must be an
  // explicit, separate, unchecked-by-default action — entering a phone number
  // cannot itself be the agreement. Never seed this true, anywhere.
  const [consented, setConsented] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      setConsented(false);
      const id = requestAnimationFrame(() => setShown(true));
      return () => cancelAnimationFrame(id);
    } else {
      setShown(false);
      const id = setTimeout(() => {
        setMounted(false);
        setStep("phone");
        setPhoneDigits("");
        setCode("");
        setConsented(false);
        setError(null);
      }, 300);
      return () => clearTimeout(id);
    }
  }, [isOpen]);

  if (!mounted) return null;

  const e164Number = `+1${phoneDigits}`;
  const formattedNumber =
    phoneDigits.length === 10
      ? `(${phoneDigits.slice(0, 3)}) ${phoneDigits.slice(3, 6)}-${phoneDigits.slice(6)}`
      : phoneDigits;

  const callSendSms = async (body: Record<string, unknown>) => {
    const resp = await fetch(SEND_SMS_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session?.access_token ?? ""}`,
        apikey: EXTERNAL_SUPABASE_PUBLISHABLE_KEY,
      },
      body: JSON.stringify(body),
    });
    const data = await resp.json().catch(() => ({}));
    return { ok: resp.ok, status: resp.status, data };
  };

  const handleSendCode = async () => {
    if (phoneDigits.length !== 10 || !consented || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const { ok, status, data } = await callSendSms({ action: "start_verify", phone_number: e164Number });
      if (!ok) {
        setError(
          status === 429
            ? "Give it a minute and tap Resend again 💜"
            : "Hmm, that didn't go through. Try again?"
        );
        return;
      }
      void data;
      setStep("code");
    } catch {
      setError("Hmm, that didn't go through. Try again?");
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    setError(null);
    await handleSendCode();
  };

  const handleVerify = async () => {
    if (code.length !== 6 || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const { ok, data } = await callSendSms({ action: "check_verify", phone_number: e164Number, code });
      if (!ok || !data?.verified) {
        setError("Hmm, that code didn't match. Try again?");
        return;
      }
      // Let the caller pick up the server-verified number (flips its toggle
      // prefs), but keep the sheet open so the "You're all set" success screen
      // can nudge her to save Kin as a contact (Issue #308) before closing.
      onVerified(e164Number);
      setStep("success");
    } catch {
      setError("Hmm, that code didn't match. Try again?");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end justify-center"
      style={{
        backgroundColor: shown ? "rgba(28,26,62,0.5)" : "rgba(28,26,62,0)",
        transition: "background-color 300ms ease-out",
      }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Set up texts"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: "#FFFFFF",
          width: "100%",
          maxWidth: 560,
          position: "relative",
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          padding: 20,
          paddingBottom: "calc(20px + env(safe-area-inset-bottom, 0px))",
          transform: shown ? "translateY(0)" : "translateY(100%)",
          transition: "transform 300ms ease-out",
          boxShadow: "0 -8px 30px rgba(28,26,62,0.18)",
        }}
      >
        <div
          style={{ width: 40, height: 4, borderRadius: 999, backgroundColor: "#C8C8F4", margin: "0 auto 16px" }}
          aria-hidden
        />
        <button
          onClick={onClose}
          aria-label="Close"
          className="flex items-center justify-center"
          style={{
            position: "absolute",
            top: 16,
            right: 16,
            width: 30,
            height: 30,
            borderRadius: "50%",
            backgroundColor: "#F0F0FF",
            border: "1px solid #E8E6F4",
            fontSize: 13,
            color: "#1C1A3E",
          }}
        >
          ✕
        </button>

        {step === "success" ? (
          <>
            <p className="font-display" style={{ fontSize: 18, color: "#1C1A3E", lineHeight: 1.25 }}>
              You're all set 💜
            </p>
            <p className="font-body" style={{ fontSize: 13, color: "#54525C", marginTop: 6, lineHeight: 1.5 }}>
              Your texts are on. That number you just heard from is me, from now on.
            </p>
            <SaveKinContactBlock variant="success" />
            <button
              onClick={onClose}
              className="font-body font-semibold w-full"
              style={{
                marginTop: 16,
                padding: "12px 16px",
                borderRadius: 999,
                fontSize: 14,
                backgroundColor: "#F0F0FF",
                color: "#1C1A3E",
                border: "1px solid #E8E6F4",
              }}
            >
              Done
            </button>
          </>
        ) : step === "phone" ? (
          <>
            <p className="font-display" style={{ fontSize: 18, color: "#1C1A3E", lineHeight: 1.25 }}>
              Want your mornings by text? 📱
            </p>
            <p className="font-body" style={{ fontSize: 13, color: "#54525C", marginTop: 6, lineHeight: 1.5 }}>
              Kin can text you your morning notes and task updates. Nothing to open, it just shows up like a friend would.
            </p>
            <input
              type="tel"
              inputMode="numeric"
              autoComplete="tel-national"
              placeholder="(555) 123-4567"
              value={formattedNumber}
              onChange={(e) => setPhoneDigits(e.target.value.replace(/\D/g, "").slice(0, 10))}
              aria-label="Phone number"
              className="font-body w-full"
              style={{
                marginTop: 16,
                border: "1.5px solid #C8C8F4",
                borderRadius: 12,
                padding: "10px 12px",
                fontSize: 15,
                color: "#1C1A3E",
              }}
            />
            {error && (
              <p className="font-body" style={{ fontSize: 12.5, color: "#DC2626", marginTop: 8 }}>{error}</p>
            )}
            <div
              onClick={() => setConsented((v) => !v)}
              className="flex cursor-pointer select-none"
              style={{ marginTop: 14, gap: 10, alignItems: "flex-start" }}
            >
              <button
                type="button"
                role="checkbox"
                aria-checked={consented}
                aria-labelledby="sms-consent-label"
                onClick={(e) => { e.stopPropagation(); setConsented((v) => !v); }}
                className="flex items-center justify-center shrink-0 transition-all"
                style={{
                  width: 20,
                  height: 20,
                  marginTop: 1,
                  borderRadius: 4,
                  border: "1.5px solid #C8C8F4",
                  backgroundColor: consented ? "#5050C8" : "transparent",
                }}
              >
                {consented && (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </button>
              <span
                id="sms-consent-label"
                className="font-body"
                style={{ fontSize: 12.5, color: "#54525C", lineHeight: 1.45 }}
              >
                Yes, text me. I agree to receive text notifications from Kin. Message frequency varies. Msg &amp; data rates may apply. Reply STOP to opt out anytime.
              </span>
            </div>
            <p className="font-body" style={{ fontSize: 11, color: "#9090A8", marginTop: 8, marginLeft: 30 }}>
              <a
                href="https://kinyoudoitall.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => { e.preventDefault(); openExternal("https://kinyoudoitall.com/privacy"); }}
                className="underline"
                style={{ color: "#5050C8" }}
              >
                Privacy Policy
              </a>
              {" · "}
              <a
                href="https://kinyoudoitall.com/terms-of-service/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => { e.preventDefault(); openExternal("https://kinyoudoitall.com/terms-of-service/"); }}
                className="underline"
                style={{ color: "#5050C8" }}
              >
                Terms
              </a>
            </p>
            <button
              onClick={handleSendCode}
              disabled={phoneDigits.length !== 10 || !consented || submitting}
              className="font-body font-semibold text-white w-full"
              style={{
                marginTop: 16,
                padding: "12px 16px",
                borderRadius: 999,
                fontSize: 14,
                backgroundColor: "#5050C8",
                opacity: phoneDigits.length !== 10 || !consented || submitting ? 0.5 : 1,
                border: "none",
              }}
            >
              {submitting ? "Sending..." : "Send my code"}
            </button>
          </>
        ) : (
          <>
            <p className="font-display" style={{ fontSize: 18, color: "#1C1A3E", lineHeight: 1.25 }}>
              Enter your code
            </p>
            <p className="font-body" style={{ fontSize: 13, color: "#54525C", marginTop: 6, lineHeight: 1.5 }}>
              Sent to {formattedNumber}. Didn't get it?{" "}
              <button
                onClick={handleResend}
                disabled={submitting}
                className="font-body font-semibold underline"
                style={{ color: "#5050C8", background: "none", border: "none", padding: 0, cursor: "pointer" }}
              >
                Resend
              </button>
            </p>
            <input
              type="tel"
              inputMode="numeric"
              placeholder="123456"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              aria-label="6-digit code"
              className="font-body w-full"
              style={{
                marginTop: 16,
                border: "1.5px solid #C8C8F4",
                borderRadius: 12,
                padding: "10px 12px",
                fontSize: 20,
                letterSpacing: 4,
                textAlign: "center",
                color: "#1C1A3E",
              }}
            />
            {error && (
              <p className="font-body" style={{ fontSize: 12.5, color: "#DC2626", marginTop: 8 }}>{error}</p>
            )}
            <button
              onClick={handleVerify}
              disabled={code.length !== 6 || submitting}
              className="font-body font-semibold text-white w-full"
              style={{
                marginTop: 16,
                padding: "12px 16px",
                borderRadius: 999,
                fontSize: 14,
                backgroundColor: "#5050C8",
                opacity: code.length !== 6 || submitting ? 0.5 : 1,
                border: "none",
              }}
            >
              {submitting ? "Verifying..." : "Verify"}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default SmsSetupSheet;
